import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import type { RequestPriority, RequestType } from '@feedback/shared';
import { strings } from '../strings';
import { styles } from './styles';
import { buildAccentStyle } from './accent';
import type { AccentConfig } from './accent';
import { Attachments } from './Attachments';
import { presentType, TypeSelector } from './TypeSelector';
import { PrioritySelector } from './PrioritySelector';
import { useFocusTrap } from './useFocusTrap';
import { CLOSE_PATH, EDGE_PANEL_ID } from './EdgeTab';
import { itemStatusPath } from '../status-link';
import { ScreenshotPreview } from './ScreenshotPreview';
import type { ScreenshotCapturer } from '../screenshot/capturer';
import { CaptureStage } from './CaptureStage';
import type { CapturePin } from './CaptureStage';
import { RecordingPanel } from './RecordingPanel';
import type { RecordingResult, ScreenRecorder } from '../recording/recorder';
import { tagAttachmentKind } from '../attachments/kind';
import { RateLimitError } from '../api/types';
import type { SubmitForm } from '../submit';
import { deriveReportTitle } from '../derive-title';

export type ModalSubmitPayload = SubmitForm;

/**
 * The capture that leads the modal, with the markers dropped on it.
 *
 * Kept OUT of `attachments` rather than seeded into it as a chip, which is
 * where it used to live. Two things follow from the separation and both are
 * the point of I19222: the capture can be retaken in place without the list
 * shuffling underneath, and its pins have somewhere to hang that survives the
 * round trip, because a `File` has nowhere to put them.
 */
export interface ModalCapture {
  blob: Blob;
  pins: CapturePin[];
}

export interface ModalSubmitResult {
  reference: string;
  /** The request id, when the ingest response carried one. The tracking link is built from it. */
  id?: string;
}

/**
 * A checkbox a host adds to the form (I22658), such as Works' consent to send
 * recent app logs. The host owns the words and any persistence: the widget
 * shows the box, reports each change, and hands every box's value to `onSubmit`.
 */
export interface HostConsent {
  id: string;
  /** A statement the box asserts or denies, so sentence case. */
  label: string;
  checked: boolean;
  onChange?: (checked: boolean) => void;
}

/** What a successful send reports to the controller, which forwards it on `fm:close`. */
export interface FiledReport {
  reference: string;
  id?: string;
}

export interface ModalProps {
  open: boolean;
  /** Type pre-selected when the modal opens (e.g. from `FeedbackTracker.open('BUG')`). */
  initialType: RequestType;
  onClose: () => void;
  /** Fired once when a report is accepted. The confirmation itself is shown in this dialog. */
  onFiled?: (filed: FiledReport) => void;
  /**
   * Origin of the client portal, from `GET /widget/config`. Null or absent
   * means the application has no client-portal sign-in, and the success step
   * shows the reference with no link: a link to a portal the reporter cannot
   * enter reads as a promise and ends at a login wall.
   */
  clientPortalUrl?: string | null;
  /**
   * Runs the actual submit (upload attachments + POST) once the form is valid and submit is
   * clicked, or Retry is pressed after a failure. The controller wires this to
   * `submitFeedback({ api, metadata, ... })` (see `../submit.ts` and `../widget-controller.ts`);
   * this component only knows it returns a `{ reference }` on success. A rejection is always
   * caught here and turned into the error/retry step — this function's promise settling (either
   * way) never escapes uncaught, honoring the widget's never-throw-into-the-host invariant.
   */
  onSubmit: (
    payload: ModalSubmitPayload,
    attachments: File[],
    capture: ModalCapture | null,
    consents: Record<string, boolean>,
  ) => Promise<ModalSubmitResult>;
  /**
   * Injected so unit tests can supply a fake instead of the real `SnapdomCapturer` (which
   * needs a real browser — see `../screenshot/capturer.ts`). The controller's default wiring
   * passes a real `SnapdomCapturer`; nothing in this component ever constructs one itself.
   */
  capturer: ScreenshotCapturer;
  /** CSS selectors masked before rasterizing — forwarded straight to `capturer.capture()`. */
  redactSelectors?: string[];
  /**
   * Appearance config the modal's accent colours (selected pill, Submit button, focus rings,
   * attachment chip) are derived from — see `./accent.ts`. Optional and defaults to `{}` so
   * call sites/tests that don't care about appearance keep rendering the original hardcoded
   * indigo unchanged.
   */
  config?: AccentConfig;
  /**
   * A screenshot captured by `App` on the launcher click, BEFORE this modal
   * mounted — see `App.tsx` for why the ordering is load-bearing. It becomes
   * the LEADING capture when the modal opens rather than an attachment chip,
   * and it deliberately skips `ScreenshotPreview`'s confirm/redact step, which
   * is reserved for a capture the user explicitly asked for.
   */
  pendingScreenshot?: Blob | null;
  /**
   * True while the screenshot behind the already-open modal is still being
   * taken (I22657). The screenshot slot shows the turning square meanwhile,
   * and the capture leads the form as soon as it lands. Absent or false means
   * whatever `pendingScreenshot` held at open is all there is.
   */
  capturePending?: boolean;
  /** Host-defined checkboxes, rendered after the recording control. */
  consents?: HostConsent[];
  /**
   * Opens a link the host's own way. A desktop host (Works) cannot follow an ordinary
   * anchor from inside its webview, so it hands the URL to its opener; a web page omits
   * this and the anchor opens a new tab.
   */
  openLink?: (url: string) => void;
  /**
   * Takes a fresh capture with the whole widget hidden, for Retake.
   *
   * Owned by `App` rather than done here, because hiding the widget means
   * reaching its shadow HOST, which this component has no reference to and
   * should not: `capturer.capture()` rasterizes `document.documentElement`, so
   * a retake run from inside an open modal photographs the modal.
   *
   * Resolves `null` when the capture failed, which leaves the existing one in
   * place. Losing a screenshot you already had because the replacement did not
   * arrive is the one outcome a retake must never produce.
   */
  onRetake?: () => Promise<Blob | null>;
  /**
   * Told about each capture the moment it leads the form, so its upload can
   * start while the reporter writes rather than after Send (I24294, ADR 0045).
   * Called again only when the BLOB changes (a retake, a manual capture), not
   * when a marker is dropped, because markers travel on the request.
   */
  onCaptureReady?: (blob: Blob) => void;
  /**
   * Injected for the same reason `capturer` is: `MediaRecorder` and
   * `getDisplayMedia` do not exist in jsdom. Absent, the recording control is
   * not offered at all, which is also what happens in a browser without the
   * API (`isRecordingSupported`).
   */
  recorder?: ScreenRecorder;
}

/**
 * Wraps a finished recording as an ordinary attachment.
 *
 * `kind: 'ATTACHMENT'`, not a third kind. A recording is a file the reporter
 * chose to send, and the one thing `SCREENSHOT` buys downstream is that the
 * admin gallery renders it as an image, which a video is not (ADR 0030).
 */
function recordingFile(result: RecordingResult): File {
  return tagAttachmentKind(
    new File([result.blob], `${strings.recording.fileLabel}-${Date.now()}.webm`, {
      type: result.blob.type || 'video/webm',
    }),
    'ATTACHMENT',
  );
}

const DESCRIPTION_MAX_LENGTH = 4000;
const HEADING_ID = 'ft-modal-heading';
const DESCRIPTION_FIELD_ID = 'ft-modal-description-field';
const DESCRIPTION_HINT_ID = 'ft-modal-description-hint';

/**
 * The feedback submission dialog, centered over a scrim, and the same dialog
 * whichever launcher opened it: the launcher style picks the trigger, never
 * the form (I22642). `onSubmit` runs the real submit flow (upload attachments,
 * assemble metadata, POST) — this component drives the visible state around that call:
 * idle (form) -> in-flight ("Sending…", fields disabled) -> success (shows the returned
 * `reference`) or error (a message above the footer, and Send again re-submits the form as
 * it now stands, so an edit made after the failure is what gets sent).
 *
 * Always mounted by `App` (not conditionally rendered) and returns `null` itself while
 * `open` is false, so the component identity — and this instance's hook state — survives
 * across a close/reopen instead of remounting. The reset below, run while rendering (I24681),
 * clears the form whenever `open` flips back to `true`, EXCEPT the description: that is the reporter's draft, and an
 * accidental close (a stray Escape, a click on the scrim) must not throw away what they typed.
 * The close button (X) is the deliberate close, so it discards the draft before closing (I24378);
 * Escape and the scrim stay accidental and keep it, and so does a host calling `close()` or
 * `hide()`, because neither is the reporter choosing to abandon what they typed. The draft is
 * also cleared when a send succeeds. Note that `Attachments` itself unmounts/remounts
 * across that close/reopen (this component's JSX returns `null` while closed), so its own
 * object-URL cache is naturally torn down too — see `Attachments.tsx`.
 */
export function Modal({
  open,
  initialType,
  onClose,
  onFiled,
  clientPortalUrl,
  onSubmit,
  onCaptureReady,
  capturer,
  redactSelectors,
  config,
  pendingScreenshot,
  capturePending,
  consents,
  openLink,
  onRetake,
  recorder,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const doneRef = useRef<HTMLButtonElement>(null);

  const [type, setType] = useState<RequestType>(presentType(initialType));
  const [priority, setPriority] = useState<RequestPriority>('NORMAL');
  const [description, setDescription] = useState('');
  // Single boolean, not a per-field map: description is the only field that can
  // be required, so a map would be one entry wide and read like there are more.
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  // Non-null exactly while the "review your screenshot" step (`ScreenshotPreview`) replaces
  // the form — set by a successful `capturer.capture()`, cleared on Confirm or Cancel.
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  // The capture that LEADS the form, with its markers. Separate from
  // `attachments` on purpose — see `ModalCapture`.
  const [capture, setCapture] = useState<ModalCapture | null>(null);
  const [retaking, setRetaking] = useState(false);
  // Shown once, after a retake that discarded markers. A marker is a point on
  // the image it was placed on, so carrying it onto a replacement keeps a
  // number pointing at whatever happens to be at those coordinates, which
  // looks deliberate and is worse than losing it (ADR 0030).
  const [pinsCleared, setPinsCleared] = useState(false);

  // The submit state machine: 'idle' is the normal editable form (also covers the screenshot
  // review step above), 'submitting' disables the fields and shows a sending indicator,
  // 'success' replaces the form with the returned reference, and 'error' keeps the form, editable,
  // with a message above the footer.
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  // Each host consent's value, seeded from what the host said and kept across
  // a close, like the draft, since the host persists its own default.
  const [consentValues, setConsentValues] = useState<Record<string, boolean>>(() =>
    Object.fromEntries((consents ?? []).map((consent) => [consent.id, consent.checked])),
  );

  /*
    The reset runs DURING the render in which `open` turns true, not in an effect after it
    (I24681). An effect runs only once the reopened dialog has been committed, so the first
    render put back whatever the last session ended on: the confirmation of a sent report, the
    previous capture. That stayed on screen until the effect ran, and `App` starts the
    screenshot raster straight after opening, which on a heavy page holds the main thread for
    as long as the capture takes, so a reporter clicking the launcher saw the confirmation
    again instead of a form. Setting state while rendering makes Preact render this component again
    before anything is committed, so the stale view never reaches the DOM at all.

    `seenOpen` starts false so a dialog MOUNTED open takes the same path. `session` counts
    opens; a send stamps it, so a send still in flight when the reporter closes the dialog
    cannot land on the next session's form (see `runSubmit`).
  */
  const [seenOpen, setSeenOpen] = useState(false);
  const session = useRef(0);
  if (open !== seenOpen) {
    setSeenOpen(open);
    if (open) {
      session.current += 1;
      setType(presentType(initialType));
      setPriority('NORMAL');
      // NOT the description: it is the draft, and survives a close (see the doc comment above).
      setDescriptionTouched(false);
      setAttachments([]);
      // Read only on this transition, so a blob arriving while the modal is already open
      // cannot yank the capture out from under someone mid-edit. `initialType` is read the
      // same way, so a caller re-rendering with a new type while open does not change the
      // reporter's selection.
      setCapture(pendingScreenshot ? { blob: pendingScreenshot, pins: [] } : null);
      setRetaking(false);
      setPinsCleared(false);
      setScreenshotBlob(null);
      setCapturing(false);
      setCaptureError(null);
      setSubmitState('idle');
      setSubmitErrorMessage(null);
      setReference(null);
      setReportId(null);
    }
  }

  // The capture taken behind the open modal leads the form the moment it lands,
  // and only then: keyed on the pending-to-done transition, so a reporter who
  // later presses Remove is not handed the same picture straight back. A
  // capture they already have (a manual one taken while this one ran) wins.
  const wasCapturePending = useRef(false);
  useEffect(() => {
    const landed = wasCapturePending.current && !capturePending;
    wasCapturePending.current = Boolean(capturePending);
    if (!open || !landed || !pendingScreenshot) return;
    setCapture((prev) => prev ?? { blob: pendingScreenshot, pins: [] });
  }, [open, capturePending, pendingScreenshot]);

  // Hand each new leading capture over for upload as soon as it is on the form.
  // Keyed on the blob, not on `capture`, which changes with every marker.
  const captureBlob = capture?.blob ?? null;
  useEffect(() => {
    if (open && captureBlob) onCaptureReady?.(captureBlob);
  }, [open, captureBlob]);

  useFocusTrap(dialogRef, open);

  // The trap above focuses the first focusable element, which is a capture control now that
  // the screenshot leads the form. The description is where a reporter starts typing, so it
  // takes focus once the trap has run. Declared after the trap so this effect runs second.
  useEffect(() => {
    if (open) descriptionRef.current?.focus();
  }, [open]);

  // The confirmation takes focus on its Done button. Send is disabled while the report is in
  // flight, and a browser drops focus from a disabled control to the page's <body>, which is
  // outside the widget's shadow root, so Escape on the confirmation never reached the handler
  // below and the dialog could only be left by mouse (I24681). A layout effect, so focus is
  // there in the same task the confirmation is committed in, not a frame later.
  useLayoutEffect(() => {
    if (open && submitState === 'success') doneRef.current?.focus();
  }, [open, submitState]);

  // Esc closes regardless of which element inside the dialog currently has focus. This used to
  // be a plain `document` listener, which is no longer reachable: `mount.ts` stops key events
  // at the widget's shadow root so a host page's global shortcuts cannot eat the widget's
  // typing, and `document` sits on the far side of that boundary. Binding to the dialog's ROOT
  // NODE puts this handler exactly ON the boundary — every key pressed anywhere inside the
  // widget still passes through it, and `mount.ts` uses `stopPropagation` rather than
  // `stopImmediatePropagation` precisely so this one keeps running. Outside a shadow root
  // (every unit test renders straight into the page) `getRootNode()` IS `document`, so the
  // original behaviour is unchanged there.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const root = dialogRef.current?.getRootNode();
    if (!root) return;
    // Typed as `Event` because `getRootNode()` returns a `Node`, whose `addEventListener` is
    // the untyped `EventTarget` one.
    const handleKeyDown = (event: Event) => {
      if ((event as KeyboardEvent).key === 'Escape') onClose();
    };
    root.addEventListener('keydown', handleKeyDown);
    return () => root.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const trimmedDescription = description.trim();
  // A marker is the reporter saying "this, here", so a marked capture is a
  // report on its own and the description stops being mandatory (I24310). A
  // capture with no marker is not: a bare screenshot from the automatic capture
  // says nothing the reporter chose, so it still needs words. The API's rule is
  // looser (any attachment will do); the widget asks for intent, not a file.
  const hasMarkers = (capture?.pins.length ?? 0) > 0;
  const descriptionRequired = !hasMarkers;
  const descriptionInvalid = descriptionRequired && descriptionTouched && trimmedDescription.length === 0;
  const isValid = trimmedDescription.length > 0 || hasMarkers;

  const handleOverlayMouseDown = (event: JSX.TargetedMouseEvent<HTMLDivElement>) => {
    // Only the overlay itself (the backdrop), never a click that started inside the dialog
    // and bubbled up, should close the modal.
    if (event.target === event.currentTarget) onClose();
  };

  // The X is the one close a reporter has to aim for, so it means "I am done with this", and
  // the draft goes with it (I24378). Escape and the scrim call `onClose` directly and keep it.
  const handleDeliberateClose = () => {
    setDescription('');
    onClose();
  };

  // The single place `onSubmit` is ever invoked — the first send and Send again both funnel
  // through here. Wrapped in try/catch (in addition to the `.catch` below) so that even a
  // synchronous throw from a misbehaving `onSubmit` can't escape into the host page; either
  // way it's turned into the error step instead of an uncaught exception.
  const runSubmit = (payload: ModalSubmitPayload) => {
    setSubmitState('submitting');
    setSubmitErrorMessage(null);
    // A reporter can close the dialog while this is in flight and open it again before it
    // lands. The outcome belongs to the session that sent it: the new form is not replaced by
    // a confirmation for a report the reporter has already moved on from (I24681).
    const sentIn = session.current;
    try {
      onSubmit(payload, attachments, capture, consentValues)
        .then((result) => {
          // The draft has been filed, so the next open starts empty. Compared with what was
          // sent, so a note typed in a later session is not the thing that gets cleared.
          setDescription((current) => (current.trim() === payload.description ? '' : current));
          onFiled?.({ reference: result.reference, id: result.id });
          if (sentIn !== session.current) return;
          setReference(result.reference);
          setReportId(result.id ?? null);
          setSubmitState('success');
        })
        .catch((err: unknown) => {
          if (sentIn !== session.current) return;
          setSubmitErrorMessage(
            err instanceof RateLimitError ? strings.submit.rateLimitedMessage : strings.submit.errorMessage,
          );
          setSubmitState('error');
        });
    } catch {
      setSubmitErrorMessage(strings.submit.errorMessage);
      setSubmitState('error');
    }
  };

  const handleSubmit = (event: JSX.TargetedEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDescriptionTouched(true);
    if (!isValid) {
      descriptionRef.current?.focus();
      return;
    }
    const payload: ModalSubmitPayload = {
      type,
      // Priority is offered only for a Problem. Every other type still sends NORMAL, which
      // is what the server writes when priority is absent, so the payload shape never varies.
      priority: type === 'BUG' ? priority : 'NORMAL',
      // The modal no longer asks for a title — see `../derive-title.ts` for why
      // one still has to exist on the wire, and where it comes from when the
      // reporter sent markers instead of a description.
      title: deriveReportTitle(trimmedDescription, capture?.pins),
      description: trimmedDescription,
    };
    runSubmit(payload);
  };

  const handleCaptureScreenshot = async () => {
    setCaptureError(null);
    setCapturing(true);
    try {
      const blob = await capturer.capture({ redactSelectors });
      setScreenshotBlob(blob);
    } catch {
      // Never let a capture failure block submitting feedback without a screenshot.
      setCaptureError(strings.screenshot.captureErrorMessage);
    } finally {
      setCapturing(false);
    }
  };

  /**
   * A manually captured shot becomes THE capture, replacing whatever led the
   * form before it.
   *
   * Replacing rather than appending, because there is one leading capture by
   * construction: two would each need their own pin list and their own retake,
   * and nothing in the request asked for that. Anyone wanting a second image
   * attaches a file.
   */
  const handleScreenshotConfirm = (finalBlob: Blob) => {
    setPinsCleared((capture?.pins.length ?? 0) > 0);
    setCapture({ blob: finalBlob, pins: [] });
    setScreenshotBlob(null);
  };

  /**
   * Replaces the capture IN PLACE, which is the whole of the retake ask.
   *
   * Everything else in the form survives: the description, the type, the
   * priority, the attachments. What does not survive is the markers, and the
   * notice says so rather than letting them silently move.
   *
   * A failed retake leaves the old capture alone. Losing a screenshot you
   * already had because its replacement did not arrive is the one outcome this
   * control must never produce.
   */
  const handleRetake = async () => {
    if (!onRetake) return;
    setRetaking(true);
    setCaptureError(null);
    try {
      const blob = await onRetake();
      if (blob) {
        setPinsCleared((capture?.pins.length ?? 0) > 0);
        setCapture({ blob, pins: [] });
      } else {
        setCaptureError(strings.screenshot.captureErrorMessage);
      }
    } finally {
      setRetaking(false);
    }
  };

  const handleRecorded = (result: RecordingResult) => {
    setAttachments((prev) => [...prev, recordingFile(result)]);
  };

  const handleScreenshotCancel = () => {
    setScreenshotBlob(null);
  };

  // Scoped to `.ft-overlay` below (this component's own root, inside the widget's closed
  // shadow root) — never `:root`/`document.documentElement`, so nothing leaks onto the host
  // page. `config` defaults to `{}`, whose every field is `undefined`; `buildAccentStyle` only
  // ever declares the two derived-tint variables (`--ft-accent-muted`/`--ft-accent-subtle`)
  // when a validly-configured `bgColor` is present, so with no config those two are left for
  // `styles.ts`'s own `var(..., <original hex>)` fallbacks to resolve — the rest of the
  // declared variables (`--ft-accent`/`--ft-accent-text`/`--ft-accent-hover`) match those
  // fallbacks' values exactly. Net effect: an unconfigured Application renders byte-identically
  // to before this task — see `accent.ts` for the full reasoning.
  const accentStyle = buildAccentStyle(config ?? {});

  return (
    <>
      <style>{styles}</style>
      <style>{accentStyle}</style>
      <div class="ft-overlay" onMouseDown={handleOverlayMouseDown}>
        <div
          // The id the edge tab's `aria-controls` names. One widget per page.
          id={EDGE_PANEL_ID}
          class="ft-dialog"
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={HEADING_ID}
          /*
            Focusable programmatically, never by Tab — `useFocusTrap`'s selector excludes
            `[tabindex="-1"]`, so the description field is still what opens focused. It is here
            so that clicking dead space inside the dialog (the heading, the padding, a label)
            lands focus on this element rather than dropping it to `document.body`. That used
            not to matter, because Esc was handled on `document` and fired wherever focus was;
            now that the handler sits on the widget's own root node, focus falling OUT of the
            widget would mean the key never reaches it. No focus ring is suppressed here and
            none is painted: `styles.ts` declares no focus rule for `.ft-dialog`, and a
            `tabindex="-1"` element focused by a click is not `:focus-visible`, so [ADR 0006]'s
            exemption is not being claimed.
          */
          tabIndex={-1}
        >
          <div class="ft-dialog-head">
            <h2 id={HEADING_ID} class="ft-dialog-heading">
              {strings.modal.heading}
            </h2>
            <p class="ft-dialog-sub">{strings.modal.subheading}</p>
          </div>

          {submitState === 'success' ? (
            <div class="ft-dialog-body">
              <div class="ft-submit-status" role="status">
                <p>{strings.submit.successMessage(reference ?? '')}</p>
                {clientPortalUrl && reportId ? (
                  <a
                    href={`${clientPortalUrl.replace(/\/+$/, '')}${itemStatusPath(reportId)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => {
                      if (!openLink) return;
                      event.preventDefault();
                      openLink((event.currentTarget as HTMLAnchorElement).href);
                    }}
                  >
                    {strings.submit.trackLabel}
                  </a>
                ) : null}
                <button ref={doneRef} type="button" class="ft-submit-button" onClick={onClose}>
                  {strings.submit.doneLabel}
                </button>
              </div>
            </div>
          ) : screenshotBlob ? (
            <div class="ft-dialog-body">
              <ScreenshotPreview
                blob={screenshotBlob}
                onConfirm={handleScreenshotConfirm}
                onCancel={handleScreenshotCancel}
              />
            </div>
          ) : (
            <form class="ft-form" onSubmit={handleSubmit} noValidate>
              {/*
                Each field/control below gets its own `disabled` prop while `submitState ===
                'submitting'` rather than wrapping in a `<fieldset disabled>` — jsdom (this
                package's test environment) doesn't implement the HTML spec's fieldset ->
                descendant-disabled propagation to the `.disabled` IDL property, so a fieldset
                would visually/semantically disable in real browsers but be untestable here.
              */}
              <div class="ft-form-fields">
                {/*
                  FIRST in the form, and that ordering is the item (I19222).
                  The capture is taken on the click that opened this modal, so
                  it already exists before anyone reads a word here. It used to
                  render as a 32px chip below the description, which asked a
                  reporter to write out what a picture in front of them already
                  said.
                */}
                {capture ? (
                  <>
                    <CaptureStage
                      blob={capture.blob}
                      pins={capture.pins}
                      onPinsChange={(pins) => setCapture((prev) => (prev ? { ...prev, pins } : prev))}
                      onRetake={() => void handleRetake()}
                      onRemove={() => {
                        setCapture(null);
                        setPinsCleared(false);
                      }}
                      retaking={retaking}
                      disabled={submitState === 'submitting'}
                    />
                    {pinsCleared ? (
                      <p class="ft-capture-notice" role="status">
                        {strings.capture.pinsClearedNotice}
                      </p>
                    ) : null}
                  </>
                ) : capturePending ? (
                  /*
                    Where the capture is about to land. One status region, so a
                    screen reader hears "Taking a screenshot" once, and the
                    square turns beside the words rather than standing in for
                    them (I22657, ADR 0035).
                  */
                  <div class="ft-capture">
                    <div class="ft-capture-head">
                      <span class="ft-capture-label">{strings.capture.heading}</span>
                    </div>
                    <div class="ft-capture-pending" role="status">
                      <span class="ft-busy-square" aria-hidden="true" />
                      <span>{strings.capture.pendingLabel}</span>
                    </div>
                  </div>
                ) : null}

                {/*
                  Type before the description, so the reporter has said what kind of
                  thing this is before writing about it. Priority belongs to a Problem
                  only: an idea or a word of praise has no urgency to grade, and
                  asking for one invites a guess that triage then has to discount.
                */}
                <TypeSelector
                  value={type}
                  onChange={(next) => {
                    setType(next);
                    if (next !== 'BUG') setPriority('NORMAL');
                  }}
                  disabled={submitState === 'submitting'}
                />

                {type === 'BUG' ? (
                  <PrioritySelector
                    value={priority}
                    onChange={setPriority}
                    disabled={submitState === 'submitting'}
                  />
                ) : null}

                <div>
                  <label class="ft-field-label" for={DESCRIPTION_FIELD_ID}>
                    {strings.fields.description.label}
                  </label>
                  <textarea
                    ref={descriptionRef}
                    id={DESCRIPTION_FIELD_ID}
                    class="ft-textarea"
                    value={description}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    placeholder={strings.fields.description.placeholder}
                    required={descriptionRequired}
                    aria-required={descriptionRequired ? 'true' : 'false'}
                    aria-invalid={descriptionInvalid || undefined}
                    aria-describedby={descriptionInvalid ? DESCRIPTION_HINT_ID : undefined}
                    onInput={(event) => setDescription((event.target as HTMLTextAreaElement).value)}
                    /*
                      Deliberately NO `onBlur` validation. Every other control in this form
                      is a sibling of this field, so moving to any of them — a type pill, a
                      priority, Add files — blurs it. Validating on blur therefore scolded
                      people for an empty description the moment they touched anything else,
                      before they had finished with the form at all. Submit is the only
                      moment the user has claimed to be done, so it is the only moment worth
                      validating.
                    */
                    disabled={submitState === 'submitting'}
                  />
                  {descriptionInvalid ? (
                    <p id={DESCRIPTION_HINT_ID} class="ft-hint" role="alert">
                      {strings.validation.descriptionRequired}
                    </p>
                  ) : null}
                </div>

                <Attachments files={attachments} onChange={setAttachments} disabled={submitState === 'submitting'} />

                <div class="ft-screenshot-trigger">
                  {/*
                    Disabled while the automatic capture is still running: a
                    second raster started beside it would race it for the
                    slot, and the reporter has nothing to retake yet.
                  */}
                  <button
                    type="button"
                    class="ft-screenshot-button"
                    onClick={handleCaptureScreenshot}
                    disabled={capturing || capturePending || submitState === 'submitting'}
                  >
                    {capturing ? strings.screenshot.capturingLabel : strings.screenshot.captureButtonLabel}
                  </button>
                  {captureError ? (
                    <p class="ft-hint" role="alert">
                      {captureError}
                    </p>
                  ) : null}
                </div>

                {/*
                  Offered only where pressing it can succeed, and the presence
                  of `recorder` IS that judgement — the controller constructs
                  one only when `isRecordingSupported()` holds, which is false
                  in every non-secure context and several mobile browsers.

                  This deliberately does NOT re-check the globals. A component
                  that takes a capability by injection and then re-derives it
                  from `navigator` makes the prop a lie: a caller who hands one
                  in has it silently ignored, which is exactly how the control
                  stayed invisible to its own test while looking wired.
                */}
                {recorder ? (
                  <RecordingPanel
                    recorder={recorder}
                    onRecorded={handleRecorded}
                    disabled={submitState === 'submitting'}
                  />
                ) : null}

                {(consents ?? []).map((consent) => (
                  <label key={consent.id} class="ft-consent">
                    <input
                      type="checkbox"
                      checked={consentValues[consent.id] ?? consent.checked}
                      disabled={submitState === 'submitting'}
                      onChange={(event) => {
                        const checked = (event.target as HTMLInputElement).checked;
                        setConsentValues((prev) => ({ ...prev, [consent.id]: checked }));
                        consent.onChange?.(checked);
                      }}
                    />
                    <span>{consent.label}</span>
                  </label>
                ))}
              </div>

              {/*
                A failed send keeps the form. It used to replace the form with the error and a
                Retry that re-sent the frozen payload, which hid the note the reporter wrote and
                ignored anything they changed after the failure.
              */}
              {submitState === 'error' && submitErrorMessage ? (
                <div class="ft-send-error">
                  <p class="ft-send-error-copy" role="alert">
                    <span class="ft-urgent" aria-hidden="true" />
                    {submitErrorMessage}
                  </p>
                </div>
              ) : null}

              {/*
                Outside the scrolling fields, so Send stays on screen.

                Disabled ONLY while a submit is in flight — not while the form is invalid.
                A disabled button cannot say why it is disabled; pressing it is how the user
                asks, and `handleSubmit` marking the field touched is the answer. Keeping it
                pressable is also what makes validation-on-submit reachable at all: gated on
                `isValid`, the hint could never appear, because the only code that sets
                `descriptionTouched` lives behind this button.
              */}
              <div class="ft-form-footer">
                <button
                  type="submit"
                  class="ft-submit-button"
                  disabled={submitState === 'submitting'}
                >
                  {submitState === 'submitting'
                    ? strings.submit.sendingLabel
                    : submitState === 'error'
                      ? strings.submit.retryLabel
                      : strings.modal.submitLabel}
                </button>
              </div>
            </form>
          )}

          {/*
            DOM-last (not first) so it isn't the container's first focusable
            element — the focus trap focuses that on open, and the description
            field above should get it, not this button. Positioned back to the
            top-right corner visually via CSS.
          */}
          <button
            type="button"
            class="ft-close-button"
            aria-label={strings.modal.closeLabel}
            /*
              Cancelling the mousedown default suppresses the focus change, and
              that is the whole fix for the close button needing two presses.
              The old sequence: mousedown -> the focused field blurs ->
              `setDescriptionTouched(true)` -> a validation hint renders -> the
              dialog gets taller -> `.ft-overlay` centres it vertically, so the
              top edge (and this absolutely-positioned button with it) moves UP
              -> mouseup lands elsewhere -> no click event, because a click
              needs both on the same element. The user got a validation error
              instead of a closed modal.

              Deliberately NOT `onMouseDown={onClose}`, which would also stop
              the double press but would break drag-away-to-cancel — pressing a
              button, changing your mind and sliding off it. Activation stays
              on click, where it belongs; keyboard Enter/Space dispatch a click
              directly and never see this handler.
            */
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleDeliberateClose}
          >
            <svg class="ft-close-glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d={CLOSE_PATH} />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}
